import { appConfig } from "../config";
import { Employee } from "./EmployeeApi";
import {apiRequest} from "../util/ApiUtils";


type EmployeeKeys = Array<keyof Employee>;

export interface TableColumnDefinition {
    attributeName: string;
    width?: number;
    align?: 'left' | 'center' | 'right' | 'inherit' | 'justify';
    sortable?: boolean;
    order: number;
    visible: boolean;
}

export interface TableColumnConfig {
    columns: TableColumnDefinition[];
    entity: string;
    userId?: string; // For user-specific configurations
}

export interface CompanySettings {
    displaySettings: {
        displayedEmployeeColumns: EmployeeKeys;
    };

    timetracking: {
        showTotalHours: boolean;
        showFte: boolean;
        workingDays: string[];
        absenceTypes: string[];
    };
}

export interface UserSettings {
    [key: string]: any;
}

export type RetrieveCompanySettingsCallback = (settings: CompanySettings) => void;

export type RetrieveUserSettingsCallback = (settings: UserSettings) => void;

export type RetrieveCompanySettingsNotFoundCallback = () => void;

export type RetrieveUserSettingsNotFoundCallback = () => void;

export type UpdateCompanySettingsCallback = (settings: CompanySettings) => void;

export type UpdateUserSettingsCallback = (settings: UserSettings) => void;


const backendUrl = appConfig.backendUrl;

export async function retrieveCompanySettings(callback: RetrieveCompanySettingsCallback, notFoundCallback: RetrieveCompanySettingsNotFoundCallback): Promise<void> {
    try {
        const response = await fetch(`${backendUrl}/api/settings/company`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            if (response.status === 404) {
                notFoundCallback();
                return;
            }

            throw new Error('Network response was not ok');
        }

        const settings: CompanySettings = await response.json();
        callback(settings);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function retrieveUserSettings(callback: RetrieveUserSettingsCallback, notFoundCallback: RetrieveUserSettingsNotFoundCallback): Promise<void> {
    try {
        const response = await fetch(`${backendUrl}/api/settings/user`, {
            method: 'GET',
            credentials: 'include'
        });

        if (!response.ok) {
            if (response.status === 404) {
                notFoundCallback();
                return;
            }

            throw new Error('Network response was not ok');
        }

        const settings: UserSettings = await response.json();
        callback(settings);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function loadPartialUserSettings(name: string): Promise<Partial<UserSettings>> {
    const data = await apiRequest(`${backendUrl}/api/settings/user/${name}`, {
        method: 'GET',
        credentials: 'include'
    });

    return data as Partial<UserSettings>;
}

export async function updateCompanySettings(settings: CompanySettings, callback: UpdateCompanySettingsCallback): Promise<void> {

    try {
        const response = await fetch(`${backendUrl}/api/settings/company`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(settings),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const updatedSettings: CompanySettings = await response.json();
        callback(updatedSettings);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function replaceUserSettings(settings: UserSettings, callback: UpdateUserSettingsCallback): Promise<void> {

    try {
        const response = await fetch(`${backendUrl}/api/settings/user`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(settings),
            credentials: 'include'
        });

        if (!response.ok) {
            throw new Error('Network response was not ok');
        }

        const updatedSettings: UserSettings = await response.json();
        callback(updatedSettings);

    } catch (error) {
        console.error('There was a problem with the fetch operation:', error);
    }
}

export async function updateUserSettings(settings: Partial<UserSettings>): Promise<Partial<UserSettings>> {

    const data = await apiRequest(`${backendUrl}/api/settings/user`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings),
        credentials: 'include'
    });

    return data as Partial<UserSettings>;
}